/**
 * Payment Calculation Utilities (web)
 *
 * Single source of truth for the PayForRide / PayTipAndTolls flows.
 * Mirrors the mobile app's `src/utils/paymentUtils.ts` (calculatePayment +
 * buildFarePayload) so the same trip + selections produces the same total
 * and the same TripCharge payload on both clients.
 *
 * Kept intentionally framework-free (no react/redux imports) so it can be
 * unit-tested in isolation and reused from anywhere.
 *
 * Charge types:
 *   - PrepayRide  : pay before/during the ride. Subtotal = fare + all fees.
 *                   Tip only when All-Inclusive. Car Cash discount allowed.
 *   - ExtraCharge : pay tip + tolls + misc after the ride. No subtotal,
 *                   no Car Cash, tip is always included.
 */

import { tripPaymentConstants } from '../../constants/tripPayment.constants';
import { formsConstants } from '../../constants/forms.constants';

const { PAYMENT_METHOD_ALL_INCLUSIVE, CHARGE_TYPE_PREPAY, CHARGE_TYPE_EXTRA_CHARGE } =
    tripPaymentConstants;

/**
 * Coerce any value (string/number/null/undefined) to a finite number.
 * Server fields are sometimes strings; this matches the defensive
 * `Number(...)` + `Number.isFinite(...)` pattern used in the mobile app.
 */
function toNumber(value) {
    const n = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(n) ? n : 0;
}

/**
 * Calculate the breakdown of what the user owes.
 *
 * Mirrors mobile `calculatePayment` in `src/utils/paymentUtils.ts`.
 *
 * @param {object}  state
 * @param {object}  state.fare              fare object from TripCharge response
 * @param {string}  state.prepayType        'AI' | 'BASE' | 'DFLT'
 * @param {object?} state.tip               selected TipOption ({ tipAmt, tipPct, override? })
 * @param {number}  [state.tolls=0]
 * @param {number}  [state.misc=0]
 * @param {boolean} [state.carCashEnabled=false]
 * @param {number}  [state.carCashAvailable=0]
 * @param {string}  chargeType              'PrepayRide' | 'ExtraCharge'
 * @returns {{subtotal:number, tipAmount:number, tollsAmount:number, miscAmount:number, carCashApplied:number, total:number}}
 */
export function calculatePayment(state, chargeType) {
    const {
        fare = {},
        prepayType,
        tip,
        tolls = 0,
        misc = 0,
        carCashEnabled = false,
        carCashAvailable = 0,
    } = state || {};

    // 1. Subtotal: PrepayRide bills the full fare + all fees. ExtraCharge
    //    does not include the subtotal because the base fare was already paid.
    const subtotal =
        chargeType === CHARGE_TYPE_PREPAY
            ? toNumber(fare.fare) +
              toNumber(fare.fees) +
              toNumber(fare.airportFee) +
              toNumber(fare.congestionFee) +
              toNumber(fare.processingFee)
            : 0;

    // 2. Tip is included on ExtraCharge always, and on PrepayRide only when
    //    the user picked All-Inclusive.
    const includeTip =
        prepayType === PAYMENT_METHOD_ALL_INCLUSIVE || chargeType === CHARGE_TYPE_EXTRA_CHARGE;
    const tipAmount = includeTip && tip ? toNumber(tip.tipAmt) : 0;

    const tollsAmount = toNumber(tolls);
    const miscAmount = toNumber(misc);

    // 3. Gross total before applying Car Cash credit.
    const grossTotal = subtotal + tipAmount + tollsAmount + miscAmount;

    // 4. Car Cash only applies on PrepayRide and is capped at the gross total
    //    (the user can't go negative).
    const carCashApplied =
        chargeType === CHARGE_TYPE_PREPAY && carCashEnabled && carCashAvailable > 0
            ? Math.min(toNumber(carCashAvailable), grossTotal)
            : 0;

    const total = Math.max(0, grossTotal - carCashApplied);

    return { subtotal, tipAmount, tollsAmount, miscAmount, carCashApplied, total };
}

/**
 * Build the `fare` object sent to the TripCharge API.
 *
 * Mirrors mobile `buildFarePayload`. Keeps the existing extra zero-valued
 * fields the web payload has historically sent so we don't change the shape
 * the backend already expects.
 */
export function buildFarePayload(state, chargeType) {
    const calc = calculatePayment(state, chargeType);
    const { fare = {}, prepayType, tip } = state || {};

    const includeTip =
        prepayType === PAYMENT_METHOD_ALL_INCLUSIVE || chargeType === CHARGE_TYPE_EXTRA_CHARGE;

    // gratuityOverride is true only when the user picked the "Other" tip
    // option (free-form amount). Matches the legacy web behavior.
    const isOtherTip = !!(tip && tip.tipPct === formsConstants.OTHER_TIP);

    const payload = {
        ...fare,

        fare: toNumber(fare.fare),
        fees: toNumber(fare.fees),
        airportFee: toNumber(fare.airportFee),
        congestionFee: toNumber(fare.congestionFee),
        processingFee: toNumber(fare.processingFee),

        gratuity: includeTip ? calc.tipAmount : 0,
        gratuityPct: includeTip ? (isOtherTip ? null : toNumber(tip && tip.tipPct)) : 0,
        gratuityOverride: includeTip ? isOtherTip : false,

        tolls: calc.tollsAmount,
        misc: calc.miscAmount,

        carCashAmt: chargeType === CHARGE_TYPE_PREPAY ? calc.carCashApplied : 0,

        // Total amount to charge (gross, before Car Cash). The mobile app
        // sends the pre-discount total here and lets the server apply the
        // Car Cash credit via carCashAmt + useCarCash. Web has historically
        // done the same so we preserve that contract.
        total: calc.subtotal + calc.tipAmount + calc.tollsAmount + calc.miscAmount,
    };

    return payload;
}

/**
 * Resolve the active tolls amount for the PrepayRide flow given the live
 * UI state. Centralized here so the fare breakdown panel, the pay button,
 * and the secondTripCharge payload always agree.
 *
 *  - ExtraCharge: from `extraChargeForm.tolls` (existing field).
 *  - PrepayRide All-Inclusive:
 *      1. user-entered override in `paymentForm.tolls` (new), if any
 *      2. server-returned tolls on the All-Inclusive fareOption
 *      3. tolls on the currently-stored `tripCharge.fare`
 *      4. zero
 *  - PrepayRide Base: zero (mobile hides the input on Base, and we mirror).
 *
 * @param {object} args
 * @param {object} args.tripCharge       state.temp.services.tripCharge
 * @param {object} args.paymentForm      state.forms[PAYMENT_FORM]
 * @param {object} args.extraChargeForm  state.forms[EXTRA_CHARGER_FORM]
 * @param {string} args.chargeType       'PrepayRide' | 'ExtraCharge'
 * @param {string} args.prepayType       'BASE' | 'AI' | undefined
 * @returns {number}
 */
export function resolveTolls({tripCharge, paymentForm, extraChargeForm, chargeType, prepayType}) {
    if (chargeType === CHARGE_TYPE_EXTRA_CHARGE && extraChargeForm) {
        return toNumber(extraChargeForm[formsConstants.EXTRA_CHARGER_TOLLS]);
    }
    if (chargeType === CHARGE_TYPE_PREPAY && prepayType === PAYMENT_METHOD_ALL_INCLUSIVE) {
        const formValue = paymentForm && paymentForm[formsConstants.PAYMENT_TOLLS];
        if (formValue !== undefined && formValue !== null && formValue !== '') {
            return toNumber(formValue);
        }
        const fareOptions = (tripCharge && Array.isArray(tripCharge.fareOptions)) ? tripCharge.fareOptions : [];
        const ai = fareOptions.find(f => f && (f.fareTypeCd === 'I' || f.fareTypeCd === 'ALL_INCLUSIVE' || f.fareTypeCd === 'AI'));
        if (ai && ai.tolls !== undefined && ai.tolls !== null) {
            return toNumber(ai.tolls);
        }
        return toNumber(tripCharge && tripCharge.fare && tripCharge.fare.tolls);
    }
    return 0;
}

export const paymentCalc = { calculatePayment, buildFarePayload, resolveTolls };
export default paymentCalc;
